import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowLeft, Landmark } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getPayoutAccounts, addPayoutAccount, updatePayoutAccount } from "../../services/walletService";
import { isValidIban } from "../../utils/iban";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Card from "../../components/common/Card";
import { color, radius } from "../../styles/theme";

/**
 * Bank Details — ported from mobile's add-bank-details screen
 * (src/Screens/add-bank-details). Payout method is fixed to "Bank Transfer"
 * (payoutMethodId: 1) and there's no bank picker — mobile's own UI locks
 * both to a single option too (see the approved plan's scope notes).
 */
const BankDetails = () => {
  const { t } = useTranslation(["wallet", "common"]);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [payoutAccountId, setPayoutAccountId] = useState(null);
  const [accountTitle, setAccountTitle] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  useEffect(() => {
    getPayoutAccounts()
      .then((res) => {
        const list = res.data?.Result || [];
        if (list.length > 0) {
          const last = list[list.length - 1];
          setIsUpdate(true);
          setPayoutAccountId(last.PayoutAccountId);
          setAccountTitle(last.AccountHolderName || "");
          setAccountNumber(last.AccountNumberMasked || "");
        }
      })
      .catch(() => toast.error(t("bankDetails.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  const handleSave = async () => {
    if (!accountTitle.trim()) {
      toast.error(t("bankDetails.accountHolderRequired"));
      return;
    }
    if (!isValidIban(accountNumber)) {
      toast.error(t("bankDetails.invalidIban"));
      return;
    }

    setSaving(true);
    try {
      const res = isUpdate
        ? await updatePayoutAccount({ payoutAccountId, accountHolderName: accountTitle, accountNumber })
        : await addPayoutAccount({ userId: user?.UserId, accountHolderName: accountTitle, accountNumber });
      toast.success(res.data?.Message || t("bankDetails.saveSuccess"));
      navigate("/wallet");
    } catch (err) {
      toast.error(err.response?.data?.Message || t("bankDetails.saveError"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => navigate("/wallet")}>
        <ArrowLeft size={13} strokeWidth={2.25} /> {t("bankDetails.backToWallet")}
      </button>
      <h2 style={s.title}>{t("bankDetails.title")}</h2>

      <Card style={s.wrap}>
        <div style={{ marginBottom: 16 }}>
          <label style={s.fieldLabel}>{t("bankDetails.payoutMethod")}</label>
          <div style={s.readonlyRow}><Landmark size={14} strokeWidth={2} color={color.gray500} /><span style={s.readonlyValue}>{t("bankDetails.bankTransfer")}</span></div>
        </div>

        <Input label={t("bankDetails.accountHolderLabel")} value={accountTitle} onChange={(e) => setAccountTitle(e.target.value)} placeholder={t("bankDetails.accountHolderPlaceholder")} />
        <Input label={t("bankDetails.ibanLabel")} value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.toUpperCase())} placeholder={t("bankDetails.ibanPlaceholder")} />

        <Button onClick={handleSave} disabled={saving} style={{ marginTop: 8 }}>
          {saving ? t("bankDetails.saving") : isUpdate ? t("bankDetails.updateBankDetails") : t("bankDetails.addBankDetails")}
        </Button>
      </Card>
    </div>
  );
};

const s = {
  linkBtn: { color: color.gray600, fontWeight: 600, marginBottom: 8, padding: "6px 10px" },
  title: { fontSize: 19, fontWeight: 800, color: color.textPrimary, marginBottom: 18, letterSpacing: -0.3 },
  wrap: { maxWidth: 420, padding: 24 },
  fieldLabel: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  readonlyRow: { display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: color.gray50, borderRadius: radius.sm },
  readonlyValue: { fontSize: 13.5, color: color.gray700, fontWeight: 500 },
};

export default BankDetails;
